<template>
  <div class="upsell-card">
    <div v-if="showAddonBadge" class="upsell-card__badge-row">
      <BaseTag tone="info" preset="primary" icon="lock">{{ resolvedBadgeLabel }}</BaseTag>
    </div>
    <h2 class="upsell-card__title">{{ title }}</h2>
    <p v-if="lede" class="upsell-card__lede">{{ lede }}</p>

    <ul v-if="benefits && benefits.length" class="upsell-card__benefits">
      <li v-for="(benefit, idx) in benefits" :key="idx" class="upsell-card__benefit">
        <span class="upsell-card__check" aria-hidden="true">
          <BaseIcon name="check" />
        </span>
        <span>{{ benefit }}</span>
      </li>
    </ul>

    <BaseInfoBanner
      class="upsell-card__banner"
      :title="$t('upsell.banner.title')"
      :description="$t('upsell.banner.description')"
    >
      <template #icon>
        <BaseIcon name="info" />
      </template>
    </BaseInfoBanner>

    <div class="upsell-card__actions">
      <BaseButton @click.native="onContactClick">
        {{ $t('upsell.actions.book-call') }}
      </BaseButton>
    </div>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import BaseButton from '@/components/base/BaseButton';
import BaseInfoBanner from '@/components/base/BaseInfoBanner';
import BaseTag from '@/components/base/BaseTag';
import { BI_CALENDAR_URL } from '@/util/tier-mapping';

export default {
  name: 'UpsellCard',
  components: { BaseIcon, BaseButton, BaseInfoBanner, BaseTag },
  props: {
    title: { type: String, default: null },
    lede: { type: String, default: null },
    benefits: { type: Array, default: () => [] },
    showAddonBadge: { type: Boolean, default: false },
    /** Label for the lock BaseTag. Defaults to upsell.addon-label when omitted. */
    badgeLabel: { type: String, default: null },
  },
  computed: {
    resolvedBadgeLabel() {
      return this.badgeLabel || this.$t('upsell.addon-label');
    },
  },
  methods: {
    onContactClick() {
      if (BI_CALENDAR_URL && BI_CALENDAR_URL !== '#') {
        window.open(BI_CALENDAR_URL, '_blank', 'noopener,noreferrer');
      }
      this.$emit('book-call');
    },
  },
};
</script>

<style lang="scss" scoped>
.upsell-card {
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 4px 12px rgba(16, 24, 40, 0.06);
  max-width: 480px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  text-align: left;
  padding: 20px 24px 24px;

  &__badge-row {
    margin-bottom: 14px;
  }

  &__title {
    font-family: $font-family-body;
    font-size: 20px;
    font-weight: $font-weight-bold;
    line-height: 1.25;
    color: $color-slate-900;
    margin: 0 0 6px;
    letter-spacing: -0.01em;
  }

  &__lede {
    font-size: 14px;
    color: $color-slate-700;
    margin: 0 0 16px;
    line-height: 1.5;
  }

  &__benefits {
    list-style: none;
    padding: 0;
    margin: 0 0 16px;
  }

  &__benefit {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 5px 0;
    font-size: 13.5px;
    color: $color-slate-700;
    line-height: 1.45;
  }

  &__check {
    flex-shrink: 0;
    margin-top: 1px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: $color-accent-500;
    color: white;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    ::v-deep svg {
      width: 11px;
      height: 11px;
      stroke-width: 3;
    }
  }

  &__banner {
    margin-bottom: 16px;
  }

  &__actions {
    display: flex;
    gap: 10px;
    justify-content: center;
  }
}
</style>
